import { parseCSV } from './csv';
import { normalizeDraftManager } from './keepers';
import { ACTIVE_MEMBERS } from './managers';

const ELIGIBLE_POSITIONS = new Set(['QB', 'RB', 'WR', 'TE']);

function emptyKeeperState() {
  return {
    published: false,
    isValid: true,
    keepers: [],
    errors: [],
  };
}

function isHtmlFallback(text) {
  return /^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text);
}

function textKey(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function receiptKey(manager, player) {
  return `${textKey(manager)}::${textKey(player)}`;
}

function readField(row, names) {
  return names.map((name) => row?.[name]).find((value) => value !== undefined && value !== null && value !== '');
}

function readNumber(row, names) {
  const value = readField(row, names);
  const number = parseInt(value, 10);
  return Number.isFinite(number) ? number : null;
}

function normalizeChoiceManager(name) {
  return normalizeDraftManager(String(name || '').trim());
}

export function buildDraftReceiptIndex(rows, managers = ACTIVE_MEMBERS) {
  const allowedManagers = new Set(managers);
  const receipts = new Map();

  rows.forEach((row) => {
    const manager = normalizeDraftManager(row.Manager);
    const player = row.Player_Name;
    const position = row.Position;
    const sourceRound = parseInt(row.Round, 10);
    const sourceOverall = parseInt(row.Pick_Overall, 10);
    const sourceDraftYear = parseInt(row.Season, 10);

    if (!allowedManagers.has(manager)) return;
    if (!player || !ELIGIBLE_POSITIONS.has(position)) return;
    if (!Number.isFinite(sourceRound) || !Number.isFinite(sourceOverall)) return;

    receipts.set(receiptKey(manager, player), {
      manager,
      player,
      position,
      nflTeam: row.NFL_Team,
      sourceDraftYear: Number.isFinite(sourceDraftYear) ? sourceDraftYear : null,
      sourceRound,
      sourceOverall,
    });
  });

  return receipts;
}

export function buildKeeperEligibilityIndex(eligibilityManagers = [], managers = ACTIVE_MEMBERS) {
  const allowedManagers = new Set(managers);
  const index = new Map();
  eligibilityManagers.forEach((managerEntry) => {
    const manager = normalizeChoiceManager(managerEntry.manager);
    if (!allowedManagers.has(manager)) return;
    (managerEntry.players || []).forEach((player) => {
      if (!player?.player) return;
      index.set(receiptKey(manager, player.player), {
        manager,
        player: player.player,
        draftedIn2025: player.draftedIn2025 === true,
        keeperCostRound: Number.isInteger(player.keeperCostRound) ? player.keeperCostRound : null,
      });
    });
  });
  return index;
}

export function validateLockedKeepers(choices, draftRows, options = {}) {
  const managers = options.managers || ACTIVE_MEMBERS;
  const sourceDraftYear = options.sourceDraftYear || 2025;
  const costDraftYear = options.costDraftYear || sourceDraftYear + 1;
  const allowedManagers = new Set(managers);
  const receiptIndex = buildDraftReceiptIndex(draftRows, managers);
  const eligibilityIndex = buildKeeperEligibilityIndex(options.eligibilityManagers || [], managers);
  const seenManagers = new Set();
  const errors = [];
  const keepers = [];

  if (!Array.isArray(choices)) {
    return {
      published: true,
      isValid: false,
      keepers: [],
      errors: ['Keeper file must be a JSON array.'],
    };
  }

  choices.forEach((choice, index) => {
    const label = `keeper[${index}]`;
    const manager = normalizeChoiceManager(readField(choice, ['manager', 'Manager']));
    const player = String(readField(choice, ['player', 'Player_Name', 'playerName']) || '').trim();
    const declaredSourceDraftYear = readNumber(choice, ['sourceDraftYear', 'source_draft_year']);
    const declaredCostDraftYear = readNumber(choice, ['costDraftYear', 'cost_draft_year']);
    const sourceRound = readNumber(choice, ['sourceRound', 'source_round', 'sourceDraftRound', 'Round']);
    const sourceOverall = readNumber(choice, ['sourceOverall', 'source_overall', 'sourcePickOverall', 'Pick_Overall']);
    const costRound = readNumber(choice, ['costRound', 'cost_round', 'costDraftRound']);
    const position = String(readField(choice, ['position', 'Position']) || '').trim();
    const nflTeam = String(readField(choice, ['nflTeam', 'NFL_Team']) || '').trim();

    if (!manager) {
      errors.push(`${label}: missing manager.`);
      return;
    }

    if (!allowedManagers.has(manager)) {
      errors.push(`${manager}: not an active manager for this keeper file.`);
    }

    if (seenManagers.has(manager)) {
      errors.push(`${manager}: duplicate keeper entry.`);
    }
    seenManagers.add(manager);

    if (!player) {
      errors.push(`${manager}: missing keeper player.`);
      return;
    }

    if (Number.isFinite(declaredSourceDraftYear) && declaredSourceDraftYear !== sourceDraftYear) {
      errors.push(`${manager}: source draft year must be ${sourceDraftYear}, not ${declaredSourceDraftYear}.`);
    }

    if (Number.isFinite(declaredCostDraftYear) && declaredCostDraftYear !== costDraftYear) {
      errors.push(`${manager}: keeper cost draft year must be ${costDraftYear}, not ${declaredCostDraftYear}.`);
    }

    const receipt = receiptIndex.get(receiptKey(manager, player));
    const eligibility = eligibilityIndex.get(receiptKey(manager, player));
    const isConfirmedNoPick = !receipt && eligibility && eligibility.draftedIn2025 === false;
    if (!receipt && !isConfirmedNoPick) {
      errors.push(`${manager}: ${player} was not found in ${sourceDraftYear} draft receipts.`);
      return;
    }

    if (isConfirmedNoPick) {
      if (Number.isFinite(sourceRound)) {
        errors.push(`${manager}: ${player} was undrafted in ${sourceDraftYear}; source round must be empty.`);
      }
      if (Number.isFinite(costRound)) {
        errors.push(`${manager}: ${player} is a no-pick keeper; cost round must be empty.`);
      }
      keepers.push({
        manager,
        player: eligibility.player,
        position: position || 'FLEX',
        nflTeam,
        sourceDraftYear,
        sourceRound: null,
        sourceOverall: null,
        costDraftYear,
        costRound: null,
        noPick: true,
      });
      return;
    }

    if (!Number.isFinite(sourceRound)) {
      errors.push(`${manager}: missing source draft round for ${player}.`);
    }

    if (!Number.isFinite(costRound)) {
      errors.push(`${manager}: missing 2026 keeper cost round for ${player}.`);
    }

    if (Number.isFinite(sourceRound) && Number.isFinite(costRound) && sourceRound !== costRound) {
      errors.push(`${manager}: ${player} cost round must match the 2025 draft round.`);
    }

    if (receipt.sourceDraftYear && receipt.sourceDraftYear !== sourceDraftYear) {
      errors.push(`${manager}: ${player} receipt is from ${receipt.sourceDraftYear}, not ${sourceDraftYear}.`);
    }

    if (Number.isFinite(sourceRound) && receipt.sourceRound !== sourceRound) {
      errors.push(`${manager}: ${player} was drafted in Round ${receipt.sourceRound}, not Round ${sourceRound}.`);
    }

    if (Number.isFinite(sourceOverall) && receipt.sourceOverall !== sourceOverall) {
      errors.push(`${manager}: ${player} was pick ${receipt.sourceOverall}, not pick ${sourceOverall}.`);
    }

    if (position && receipt.position !== position) {
      errors.push(`${manager}: ${player} position is ${receipt.position}, not ${position}.`);
    }

    if (nflTeam && receipt.nflTeam !== nflTeam) {
      errors.push(`${manager}: ${player} NFL team is ${receipt.nflTeam}, not ${nflTeam}.`);
    }

    keepers.push({
      manager,
      player: receipt.player,
      position: receipt.position,
      nflTeam: receipt.nflTeam,
      sourceDraftYear,
      sourceRound: receipt.sourceRound,
      sourceOverall: receipt.sourceOverall,
      costDraftYear,
      costRound: Number.isFinite(costRound) ? costRound : receipt.sourceRound,
      noPick: false,
    });
  });

  managers.forEach((manager) => {
    if (!seenManagers.has(manager)) {
      errors.push(`${manager}: missing locked keeper.`);
    }
  });

  return {
    published: true,
    isValid: errors.length === 0,
    keepers,
    errors,
  };
}

export async function loadLockedKeepers(year = 2026, options = {}) {
  const {
    fetchImpl = fetch,
    managers = ACTIVE_MEMBERS,
    sourceDraftYear = year - 1,
    costDraftYear = year,
  } = options;

  const keeperResponse = await fetchImpl(`/data/keepers/${year}.json`);
  if (!keeperResponse.ok) return emptyKeeperState();

  const keeperText = await keeperResponse.text();
  if (!keeperText || isHtmlFallback(keeperText)) return emptyKeeperState();

  let choices;
  try {
    choices = JSON.parse(keeperText);
  } catch {
    return {
      published: true,
      isValid: false,
      keepers: [],
      errors: [`Keeper file for ${year} is not valid JSON.`],
    };
  }

  const draftResponse = await fetchImpl(`/draft_data/${sourceDraftYear}_draft.csv`);
  if (!draftResponse.ok) {
    return {
      published: true,
      isValid: false,
      keepers: [],
      errors: [`Missing ${sourceDraftYear} draft receipts for keeper validation.`],
    };
  }

  const draftText = await draftResponse.text();
  if (!draftText || isHtmlFallback(draftText)) {
    return {
      published: true,
      isValid: false,
      keepers: [],
      errors: [`Missing ${sourceDraftYear} draft receipts for keeper validation.`],
    };
  }

  let eligibilityManagers = [];
  const eligibilityResponse = await fetchImpl(`/data/keeper-eligibility/${year}.json`);
  if (eligibilityResponse?.ok) {
    const eligibilityText = await eligibilityResponse.text();
    if (eligibilityText && !isHtmlFallback(eligibilityText)) {
      try {
        eligibilityManagers = JSON.parse(eligibilityText).managers || [];
      } catch {
        eligibilityManagers = [];
      }
    }
  }

  return validateLockedKeepers(choices, parseCSV(draftText), {
    managers,
    sourceDraftYear,
    costDraftYear,
    eligibilityManagers,
  });
}
